<template>
  <div class="group-permissions-management">
    <title-panel
      :no-counter="true"
      :title="t('workspaceGroupPermissions.title')"
    />
    <p class="permissions-description">
      {{ t('workspaceGroupPermissions.description') }}
    </p>
    <!-- HACK: The PrimeVue Select component is used to force loading of -->
    <!-- needed PrimeVue styles. -->
    <PVSelect :style="{ display: 'none' }" />
    <div class="permissions-row">
      <div v-if="location" class="kks-selector-wrapper">
        <kks-selector
          :key="selectorKey"
          v-model="kksSelection"
          :busy="isLoading || isSavingWorkspaceGroupKksRange"
          :can-remove-last="false"
          :disabled="isSelectorDisabled"
          :label="t('workspaceGroupPermissions.kksSelectorLabel')"
          :location="location"
          :show-filter-button="false"
          @change="handleKksChanged"
        />
      </div>
      <PVButton
        class="remove-button"
        :disabled="!selectedStartNode || isRemovingWorkspaceGroupKksRange"
        :loading="isRemovingWorkspaceGroupKksRange"
        severity="danger"
        @click="handleRemove"
      >
        {{ t('workspaceGroupPermissions.remove') }}
      </PVButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { computed, nextTick, onMounted, ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useWorkspaceGroupKksScopes } from '@/composables/api/workspaces/useWorkspaceGroupKksScopes';
  import { useGetWorkspace } from '@/composables/api/workspaces/useGetWorkspace';
  import { KksSelectorChangeEvent } from '@/components/TreeSelect/KksSelector.types';
  import TitlePanel from '@/components/TitlePanel/TitlePanel.vue';

  const props = defineProps<{
    workspaceId: string;
    groupId: string;
  }>();

  const { t } = useI18n();
  const { workspace } = useGetWorkspace(props.workspaceId);

  const {
    workspaceGroupKksRange,
    fetchWorkspaceGroupKksRange,
    isLoadingWorkspaceGroupKksRange,
    isSavingWorkspaceGroupKksRange,
    saveWorkspaceGroupKksRange,
    isRemovingWorkspaceGroupKksRange,
    removeWorkspaceGroupKksRange,
  } = useWorkspaceGroupKksScopes(props.groupId);

  const kksSelection = ref<string>('');
  const selectorKey = ref(0);
  const isSyncingSelection = ref(false);
  const pendingStartNode = ref<string | null>(null);
  const selectedStartNode = computed(
    () => workspaceGroupKksRange.value?.startNode ?? null,
  );
  const effectiveStartNode = computed(
    () => pendingStartNode.value ?? selectedStartNode.value,
  );
  const location = computed(() => workspace.value?.location ?? '');
  const isLoading = computed(() => isLoadingWorkspaceGroupKksRange.value);

  const isSelectorDisabled = computed(
    () =>
      Boolean(effectiveStartNode.value) || isSavingWorkspaceGroupKksRange.value,
  );

  function toKksValue(startNode: string | null) {
    return startNode || '';
  }

  async function syncSelection(startNode: string | null) {
    isSyncingSelection.value = true;
    kksSelection.value = toKksValue(startNode);
    selectorKey.value += 1;
    await nextTick();
    isSyncingSelection.value = false;
  }

  async function handleKksChanged(event: KksSelectorChangeEvent) {
    if (isSyncingSelection.value) return;
    const selected = event.target.value.map((kks) => kks.kks_trimmed);
    const [startNode] = selected;
    if (!startNode) return;
    if (startNode === selectedStartNode.value) return;
    if (startNode === pendingStartNode.value) return;

    pendingStartNode.value = startNode;
    await syncSelection(startNode);
    const success = await saveWorkspaceGroupKksRange(startNode);
    if (success) {
      await fetchWorkspaceGroupKksRange();
    }
    pendingStartNode.value = null;
  }

  async function handleRemove() {
    if (!selectedStartNode.value) return;
    const success = await removeWorkspaceGroupKksRange();
    if (success) {
      await fetchWorkspaceGroupKksRange();
      await syncSelection(null);
    }
  }

  onMounted(async () => {
    await fetchWorkspaceGroupKksRange();
    await syncSelection(selectedStartNode.value);
  });
</script>

<style scoped lang="scss">
  .group-permissions-management {
    margin-top: 2rem;
  }
  .permissions-description {
    margin-bottom: 1rem;
  }
  .permissions-row {
    max-width: 600px;
    display: flex;
    align-items: center;
    gap: 2rem;
    flex-wrap: wrap;
  }
  .kks-selector-wrapper {
    flex: 1 1 320px;
    min-width: 260px;
  }
</style>
